import { create } from "zustand";
import { combine } from "zustand/middleware";
import type { GraspCandidate } from "../types/grasps";
import { resolveCritterAssetsUrl } from "../utils/assetPaths";

type GraspState = {
  /** Maps body UUID → array of GraspCandidates (object-local frame, Z-up). */
  graspsByBodyUuid: Record<string, GraspCandidate[]>;
};

type GraspActions = {
  /**
   * Fetch objects/<objectFolder>/grasps.json and store the result keyed by
   * bodyUuid. Silently no-ops on 404 (object has no grasp data).
   */
  loadGraspsForBody: (
    bodyUuid: string,
    objectFolder: string,
    options?: { source?: string }
  ) => Promise<void>;
  clearGraspsForBody: (bodyUuid: string) => void;
  clearAllGrasps: () => void;
};

const initialState = (): GraspState => ({
  graspsByBodyUuid: {},
});

export const useGraspStore = create<GraspState & { actions: GraspActions }>()(
  combine(initialState(), (set) => ({
    actions: {
      loadGraspsForBody: async (bodyUuid, objectFolder, options) => {
        try {
          const path = `/objects/${objectFolder}/grasps.json`;
          const url = options?.source === "critter-assets" ? resolveCritterAssetsUrl(path) : path;
          const res = await fetch(url, {
            cache: "no-store",
          });
          if (!res.ok) return; // 404 = no grasp data for this object
          const grasps: GraspCandidate[] = await res.json();
          if (!Array.isArray(grasps) || grasps.length === 0) return;
          set((state) => ({
            graspsByBodyUuid: {
              ...state.graspsByBodyUuid,
              [bodyUuid]: grasps,
            },
          }));
        } catch {
          // Network error or malformed JSON — fail silently
        }
      },

      clearGraspsForBody: (bodyUuid) =>
        set((state) => {
          const next = { ...state.graspsByBodyUuid };
          delete next[bodyUuid];
          return { graspsByBodyUuid: next };
        }),

      clearAllGrasps: () => set({ graspsByBodyUuid: {} }),
    },
  }))
);

export const getGraspState = () => {
  const { actions, ...state } = useGraspStore.getState();
  return state;
};
export const getGraspActions = () => useGraspStore.getState().actions;
